/**
 * Shared types, helpers, and constants used across view components.
 * Extracted from App.tsx to avoid duplication.
 */

import type { CivicSyncStatus, CollectionRepresentative, UserAddress, UserSettings } from '../lib/types'
import { getProviderScope } from '../lib/providers'
import { formatStateBillTitle } from '../lib/utils'

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

/** Four-bucket alignment state shown as the Support/Mixed/Neutral/Oppose pill. */
export type AlignmentState = 'support' | 'mixed' | 'neutral' | 'oppose'

export type DisplayBill = {
  id: string
  provider_id: string
  title: string
  status: string
  last_action: string
  date: string
  sponsor?: string
  related_candidates?: string[]
  priority: 'critical' | 'high' | 'medium' | 'low'
  type: 'legislation' | 'tracking' | 'alert'
  matter_type?: string | null
  tracking_state?: string
  is_failed?: boolean
  user_alignment_score?: number | null
  user_alignment_state?: 'support' | 'mixed' | 'neutral' | 'oppose' | null
  saved: boolean
}

export type BillOutcome = 'in_progress' | 'stalled' | 'passed' | 'failed'

export function deriveBillOutcome(bill: { tracking_state?: string; is_failed?: boolean }): BillOutcome {
  if (bill.is_failed) return 'failed'
  const ts = String(bill.tracking_state || '').toLowerCase()
  if (ts === 'enacted') return 'passed'
  if (ts === 'stalled') return 'stalled'
  return 'in_progress'
}

export const OUTCOME_BADGE_CLASSES: Record<BillOutcome, string> = {
  passed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  stalled: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-sky-100 text-sky-700',
}

export const OUTCOME_BADGE_LABELS: Record<BillOutcome, string> = {
  passed: 'Passed',
  failed: 'Failed',
  stalled: 'Stalled',
  in_progress: 'In Progress',
}

const FEDERAL_ID_LABELS: Record<string, string> = {
  hr: 'H.R.',
  s: 'S.',
  hres: 'H.Res.',
  sres: 'S.Res.',
  hjres: 'H.J.Res.',
  sjres: 'S.J.Res.',
  hconres: 'H.Con.Res.',
  sconres: 'S.Con.Res.',
}

/** Human-readable list title: federal ids become "H.R. 123 (119th): …",
 * state ids go through formatStateBillTitle, everything else falls back
 * to the raw title. Shared by the Bills list and the dashboard. */
export function formatBillListTitle(providerId: string, title: string): string {
  const pid = String(providerId || '').trim()
  const billTitle = String(title || '').trim()
  if (!pid) return billTitle || 'Untitled bill'

  const federalMatch = pid.match(/^([a-z]+)(\d+)-(\d+)$/i)
  if (federalMatch) {
    const billType = String(federalMatch[1] || '').toLowerCase()
    const number = String(federalMatch[2] || '')
    const congress = String(federalMatch[3] || '')
    const label = FEDERAL_ID_LABELS[billType]
    if (label) {
      return `${label} ${number} (${congress}th): ${billTitle || pid}`
    }
  }

  const stateTitle = formatStateBillTitle(pid, billTitle)
  if (stateTitle) return stateTitle

  return billTitle || pid
}

export type RepresentativeLevel = 'federal' | 'state' | 'county' | 'municipal' | 'other'

export type RepresentativeRow = {
  representative: CollectionRepresentative
  level: RepresentativeLevel
  officeTitle: string
  federalOfficeGroup: FederalOfficeGroup
}

export type FederalOfficeGroup = 'president' | 'senate' | 'house' | 'other'
export type BillLevelKey = 'federal' | 'state' | 'county' | 'municipal'

export const REPRESENTATIVES_FETCH_LIMIT = 2500

export const DEFAULT_ADDRESS: UserAddress = {
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function hasAddressConfigured(settings: UserSettings | null): boolean {
  if (!settings) return false
  return Boolean(
    settings.address.address_line1.trim() &&
      settings.address.city.trim() &&
      settings.address.state.trim(),
  )
}

export function getDistrictSummary(settings: UserSettings | null): string | undefined {
  if (!settings) return undefined
  const federal = settings.districts.find((d) => d.level === 'federal')
  if (federal) return federal.name
  const state = settings.districts.find((d) => d.level === 'state')
  if (state) return state.name
  const local = settings.districts.find((d) => d.level === 'local')
  if (local) return local.name
  return undefined
}

export function initialsFromName(name: string | undefined | null, max = 3): string {
  if (!name) return '?'
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return parts
    .slice(0, max)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

export function formatRelativeTime(isoDate?: string | null): string {
  if (!isoDate) return 'Never'
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return 'Unknown'
  const diffMs = Date.now() - date.getTime()
  const diffMins = Math.max(0, Math.floor(diffMs / 60000))
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const hours = Math.floor(diffMins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function formatTimestamp(isoDate?: string | null): string {
  if (!isoDate) return 'N/A'
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleString()
}

export function clampIdeologyScore(value: number | null | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0
  return Math.max(-1, Math.min(1, value))
}

export function ideologyPointerLeft(score: number | null | undefined): string {
  const normalized = clampIdeologyScore(score)
  const percent = ((normalized + 1) / 2) * 100
  return `${percent.toFixed(1)}%`
}

export function getOfficeTitle(office: string | undefined): string {
  const normalized = String(office || '').trim().toUpperCase()
  if (normalized === 'P') return 'President'
  if (normalized === 'S') return 'U.S. Senate'
  if (normalized === 'H') return 'U.S. House'
  return normalized || 'Public Office'
}

export function getRepresentativeLevel(rep: CollectionRepresentative): RepresentativeLevel {
  const explicitLevel = String(rep.level || '').toLowerCase()
  if (
    explicitLevel === 'federal' ||
    explicitLevel === 'state' ||
    explicitLevel === 'county' ||
    explicitLevel === 'municipal'
  ) {
    return explicitLevel
  }

  if (explicitLevel === 'local') {
    const provider = String(rep.provider || '').toLowerCase()
    const officeText = String(rep.office || '').toLowerCase()
    const chamberText = String(rep.chamber || '').toLowerCase()
    if (getProviderScope(provider) === 'county' || /county/.test(officeText) || /county/.test(chamberText)) {
      return 'county'
    }
    return 'municipal'
  }

  const office = String(rep.office || '').toUpperCase()
  const chamber = String(rep.chamber || '').toLowerCase()
  if (office === 'P' || office === 'S' || office === 'H') return 'federal'
  if (chamber === 'house' || chamber === 'senate') return 'federal'

  const raw = `${rep.office ?? ''}`.toLowerCase()
  if (/(governor|state|assembly|legislature|senate|house)/.test(raw)) return 'state'
  if (/(county|commissioner)/.test(raw)) return 'county'
  if (/(mayor|city|town|village|ward|council|school|board|clerk|supervisor|alder|municipal)/.test(raw)) return 'municipal'
  return 'other'
}

export function getRepresentativeLevelLabel(level: RepresentativeLevel): string {
  if (level === 'federal') return 'Federal'
  if (level === 'state') return 'State'
  if (level === 'county') return 'County'
  if (level === 'municipal') return 'Municipal'
  return 'Other'
}

export function getFederalOfficeGroup(representative: CollectionRepresentative): FederalOfficeGroup {
  const fromApi = String(representative.federal_office_group || '').toLowerCase()
  if (fromApi === 'president' || fromApi === 'senate' || fromApi === 'house') {
    return fromApi
  }

  const office = String(representative.office || '').toUpperCase()
  const chamber = String(representative.chamber || '').toLowerCase()
  if (office === 'P') return 'president'
  if (office === 'S' || chamber === 'senate') return 'senate'
  if (office === 'H' || chamber === 'house') return 'house'
  return 'other'
}

export function normalizePriority(priority: string | undefined): 'critical' | 'high' | 'medium' | 'low' {
  if (priority === 'critical' || priority === 'high' || priority === 'medium' || priority === 'low') return priority
  return 'medium'
}

export function normalizeBillType(type: string | undefined): 'legislation' | 'tracking' | 'alert' {
  if (type === 'legislation' || type === 'tracking' || type === 'alert') return type
  return 'tracking'
}

/** Compute derived sync progress values from sync status. */
export function deriveSyncProgress(civicSyncStatus: CivicSyncStatus | null) {
  const latestSyncRun = civicSyncStatus?.latest_success ?? civicSyncStatus?.latest
  const latestRunDetails = civicSyncStatus?.latest?.details ?? {}
  const latestProgressStep = String(latestRunDetails.progress_step || '')
  const latestProgressMessage = String(latestRunDetails.progress_message || '')
  const latestProgressPercent = Number(latestRunDetails.progress_percent ?? 0)
  const latestHeartbeatAt = String(latestRunDetails.heartbeat_at || '')
  const latestEnrichmentErrors = Array.isArray(latestRunDetails.enrichment_errors)
    ? (latestRunDetails.enrichment_errors as string[])
    : []
  const civicLastUpdatedAt = latestSyncRun?.completed_at ?? latestSyncRun?.started_at

  return {
    latestSyncRun,
    latestProgressStep,
    latestProgressMessage,
    latestProgressPercent,
    latestHeartbeatAt,
    latestEnrichmentErrors,
    civicLastUpdatedAt,
  }
}

export function deriveCivicState(
  civicBusy: boolean,
  civicSyncStatus: CivicSyncStatus | null,
): 'syncing' | 'error' | 'idle' {
  if (civicBusy || civicSyncStatus?.is_running) return 'syncing'
  if (civicSyncStatus?.latest?.status === 'failed') return 'error'
  return 'idle'
}

/** Build representative rows from raw list. */
export function buildRepresentativeRows(
  civicRepresentatives: CollectionRepresentative[],
): RepresentativeRow[] {
  return civicRepresentatives
    .filter((representative) => Boolean(representative.active ?? true))
    .map((representative) => {
      const level = getRepresentativeLevel(representative)
      const federalOfficeGroup = level === 'federal' ? getFederalOfficeGroup(representative) : 'other'
      return {
        representative,
        level,
        officeTitle: getOfficeTitle(representative.office),
        federalOfficeGroup,
      }
    })
}

export function groupRepresentativeRowsByLevel(rows: RepresentativeRow[]) {
  return {
    federal: rows.filter((row) => row.level === 'federal'),
    state: rows.filter((row) => row.level === 'state'),
    county: rows.filter((row) => row.level === 'county'),
    municipal: rows.filter((row) => row.level === 'municipal'),
    other: rows.filter((row) => row.level === 'other'),
  }
}

export function groupFederalRowsByOffice(federalRows: RepresentativeRow[]) {
  return {
    president: federalRows.filter((row) => row.federalOfficeGroup === 'president'),
    senate: federalRows.filter((row) => row.federalOfficeGroup === 'senate'),
    house: federalRows.filter((row) => row.federalOfficeGroup === 'house'),
    other: federalRows.filter((row) => row.federalOfficeGroup === 'other'),
  }
}
